// client/src/components/services_tester/ImageLangPanel.jsx
//
// Полная панель ImageLang v10.8:
//   - «Полный отчёт» (comprehensive): summary + список всех секций (клик → детали)
//   - «Encode»: features (JSON) → bit_id
//   - «Decode»: bit_id → features
//   - Компактный MUI, как в Cabinet.
//

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Tabs,
  Tab,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Code as CodeIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';
import Gateway from '../../api/gateway';

// ============================================================================
// Константы
// ============================================================================

const TABS = {
  REPORT: 0,
  ENCODE: 1,
  DECODE: 2,
  SECTION: 3,
};

const DEFAULT_ENCODE_FEATURES = {
  type: 'object',
  complexity: 'сложный',
  life: 'живое',
  scale: 'норма',
  race_code: 'Terran_Homo_Sapiens',
  gender: 'male',
  height_class: 'average',
  weight_class: 'average',
  build_class: 'normal',
  brightness: 'яркий',
  distance: 'близко',
};

const STATUS_COLORS = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
};

// ============================================================================
// Хелперы
// ============================================================================

function statusIcon(status) {
  if (status === 'ok') return <CheckIcon fontSize="small" color="success" />;
  if (status === 'error') return <CancelIcon fontSize="small" color="error" />;
  return <BugIcon fontSize="small" color="warning" />;
}

function formatMs(ms) {
  if (!ms && ms !== 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

// ============================================================================
// Таб «Полный отчёт»
// ============================================================================

function FullReportTab({ report, loading, error, onRefresh }) {
  if (loading && !report) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1 }}>
        <CircularProgress size={24} />
        <Typography variant="caption" color="text.secondary">
          Запуск диагностики (может занять ~3–5 сек)…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!report) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Отчёт ещё не загружен.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayIcon />}
          onClick={onRefresh}
        >
          Запустить диагностику
        </Button>
      </Box>
    );
  }

  const { summary = {}, sections = [] } = report;

  return (
    <Box sx={{ p: 1 }}>
      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            label={report.status || summary.status || 'unknown'}
            color={STATUS_COLORS[report.status || summary.status] || 'default'}
            size="small"
            sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22 }}
          />
          <Chip
            label={`${summary.passed ?? 0}/${summary.total_checks ?? 0}`}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.65rem' }}
          />
          <Chip
            label={`${summary.success_rate ?? 0}%`}
            color={summary.success_rate >= 99 ? 'success' : 'warning'}
            size="small"
            variant="outlined"
            sx={{ height: 22, fontSize: '0.65rem' }}
          />
          {summary.failed > 0 && (
            <Chip
              label={`failed: ${summary.failed}`}
              color="error"
              size="small"
              sx={{ height: 22, fontSize: '0.65rem' }}
            />
          )}
          {summary.failed_sections > 0 && (
            <Chip
              label={`failed sections: ${summary.failed_sections}`}
              color="error"
              size="small"
              sx={{ height: 22, fontSize: '0.65rem' }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {formatMs(report.diagnostics_time_ms)}
          </Typography>
          <Tooltip title="Перезапустить">
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Секции */}
      <Stack spacing={0.5}>
        {sections.map((s) => {
          const isOk = s.status === 'ok';
          const color = isOk ? 'success' : s.status === 'error' ? 'error' : 'warning';

          return (
            <Accordion
              key={s.name}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                  <Chip
                    label={s.status === 'ok' ? 'ok' : 'error'}
                    color={color}
                    size="small"
                    sx={{ height: 18, fontSize: '0.6rem', minWidth: 50 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem', flex: 1 }}
                  >
                    {s.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {s.passed ?? 0}/{s.total ?? 0}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1, pt: 0 }}>
                {s.error ? (
                  <Alert severity="error" sx={{ fontSize: '0.7rem', py: 0.5 }}>
                    {s.error}
                  </Alert>
                ) : (
                  <Stack spacing={0.25}>
                    {(s.checks || []).map((c, idx) => (
                      <Stack
                        key={`${s.name}-${idx}`}
                        direction="row"
                        spacing={0.5}
                        alignItems="flex-start"
                      >
                        {statusIcon(c.status)}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: '0.65rem', display: 'block', wordBreak: 'break-word' }}
                          >
                            {c.label}
                          </Typography>
                          {c.details && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.6rem',
                                display: 'block',
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {c.details}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>
    </Box>
  );
}

// ============================================================================
// Таб «Encode»
// ============================================================================

function EncodeTab({ onEncode, encodeResult, encodeLoading, encodeError }) {
  const [input, setInput] = useState(JSON.stringify(DEFAULT_ENCODE_FEATURES, null, 2));

  const handleEncode = () => {
    try {
      const features = JSON.parse(input);
      onEncode(features);
    } catch (e) {
      onEncode(null, e.message);
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      <TextField
        label="Признаки образа (JSON)"
        multiline
        minRows={6}
        maxRows={12}
        fullWidth
        size="small"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        sx={{
          mb: 1,
          '& textarea': { fontFamily: 'monospace', fontSize: '0.7rem' },
        }}
      />
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<CodeIcon />}
          onClick={handleEncode}
          disabled={encodeLoading}
        >
          {encodeLoading ? 'Кодирование…' : 'Кодировать'}
        </Button>
      </Stack>

      {encodeError && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '0.7rem' }}>
          {encodeError}
        </Alert>
      )}

      {encodeResult && (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Результат
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            <Stack direction="row" spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                bit_id:
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}
              >
                {encodeResult.bit_id}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                decimal:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                {encodeResult.decimal}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                hex:
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                {encodeResult.hex}
              </Typography>
            </Stack>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
                binary:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.65rem',
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                {encodeResult.binary}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

// ============================================================================
// Таб «Decode»
// ============================================================================

function DecodeTab({ onDecode, decodeResult, decodeLoading, decodeError }) {
  const [bitIdInput, setBitIdInput] = useState('');

  const handleDecode = () => {
    const trimmed = bitIdInput.trim();
    if (!trimmed) {
      onDecode(null, 'Введите bit_id');
      return;
    }
    if (!/^\d+$/.test(trimmed)) {
      onDecode(null, 'bit_id должен быть положительным целым числом');
      return;
    }
    onDecode(trimmed);
  };

  return (
    <Box sx={{ p: 1 }}>
      <TextField
        label="bit_id (целое число)"
        fullWidth
        size="small"
        value={bitIdInput}
        onChange={(e) => setBitIdInput(e.target.value)}
        sx={{ mb: 1, '& input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
      />
      <Button
        variant="contained"
        size="small"
        startIcon={<CodeIcon />}
        onClick={handleDecode}
        disabled={decodeLoading}
        sx={{ mb: 1 }}
      >
        {decodeLoading ? 'Декодирование…' : 'Декодировать'}
      </Button>

      {decodeError && (
        <Alert severity="error" sx={{ mb: 1, fontSize: '0.7rem' }}>
          {decodeError}
        </Alert>
      )}

      {decodeResult && (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Признаки
          </Typography>
          <Box
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.65rem',
              m: 0,
              mt: 0.5,
              p: 0.5,
              bgcolor: 'action.hover',
              borderRadius: 0.5,
              overflow: 'auto',
              maxHeight: 300,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(decodeResult.features || decodeResult, null, 2)}
          </Box>
        </Paper>
      )}
    </Box>
  );
}

// ============================================================================
// Таб «Секция» (произвольная)
// ============================================================================

function SectionTab({ sectionName, sectionData, sectionLoading, sectionError, onLoad }) {
  const [name, setName] = useState(sectionName || 'encoder_decoder');

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField
          label="Имя секции"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ flex: 1, '& input': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayIcon />}
          onClick={() => onLoad(name)}
          disabled={sectionLoading || !name.trim()}
        >
          {sectionLoading ? '…' : 'Загрузить'}
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
        Примеры: semantic_dictionary, ets_tables, feature_tree_manager, encoder_decoder, identify_entity, world_init
      </Typography>

      {sectionError && (
        <Alert severity="error" sx={{ mt: 1, fontSize: '0.7rem' }}>
          {sectionError}
        </Alert>
      )}

      {sectionData && (
        <Paper variant="outlined" sx={{ p: 1, mt: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip
              label={`${sectionData.passed}/${sectionData.total}`}
              color={sectionData.failed === 0 ? 'success' : 'warning'}
              size="small"
              sx={{ height: 20, fontSize: '0.6rem' }}
            />
            {sectionData.failed > 0 && (
              <Chip
                label={`failed: ${sectionData.failed}`}
                color="error"
                size="small"
                sx={{ height: 20, fontSize: '0.6rem' }}
              />
            )}
          </Stack>

          <Divider sx={{ my: 0.5 }} />

          <Stack spacing={0.25}>
            {(sectionData.checks || []).map((c, idx) => (
              <Stack key={idx} direction="row" spacing={0.5} alignItems="flex-start">
                {statusIcon(c.status)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.65rem', display: 'block', wordBreak: 'break-word' }}
                  >
                    {c.label}
                  </Typography>
                  {c.details && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.6rem',
                        display: 'block',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {c.details}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

// ============================================================================
// Основной компонент
// ============================================================================

export default function ImageLangPanel({ onEncode, onDecode, encodeResult, decodeResult }) {
  const [tab, setTab] = useState(TABS.REPORT);

  // Report
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  // Encode / Decode
  const [encodeLoading, setEncodeLoading] = useState(false);
  const [encodeError, setEncodeError] = useState(null);
  const [decodeLoading, setDecodeLoading] = useState(false);
  const [decodeError, setDecodeError] = useState(null);

  // Section
  const [sectionName, setSectionName] = useState('encoder_decoder');
  const [sectionData, setSectionData] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionError, setSectionError] = useState(null);

  // ---- Report ----
  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const res = await Gateway.http.imcon.get('/api/health/image_lang/comprehensive');
      const data = res?.data?.data || res?.data;
      if (data && data.sections) {
        setReport(data);
      } else {
        setReportError('Неожиданный ответ сервера');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      setReportError(
        status === 401 ? 'Сессия истекла. Войдите заново.' : `Ошибка: ${msg}`
      );
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === TABS.REPORT && !report && !reportLoading) {
      loadReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- Encode ----
  const handleEncode = useCallback(
    async (features, errorMsg) => {
      if (errorMsg) {
        setEncodeError(errorMsg);
        return;
      }
      setEncodeLoading(true);
      setEncodeError(null);
      try {
        const res = await Gateway.http.imcon.post('/api/image-lang/encode', { features });
        const data = res?.data;
        if (data?.success) {
          if (onEncode) onEncode(data);
        } else {
          setEncodeError(data?.error || 'Ошибка кодирования');
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        setEncodeError(msg);
      } finally {
        setEncodeLoading(false);
      }
    },
    [onEncode]
  );

  // ---- Decode ----
  const handleDecode = useCallback(
    async (bitId, errorMsg) => {
      if (errorMsg) {
        setDecodeError(errorMsg);
        return;
      }
      setDecodeLoading(true);
      setDecodeError(null);
      try {
        const res = await Gateway.http.imcon.post('/api/image-lang/decode', {
          bit_id: bitId,
        });
        const data = res?.data;
        if (data?.success) {
          if (onDecode) onDecode(data);
        } else {
          setDecodeError(data?.error || 'Ошибка декодирования');
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        setDecodeError(msg);
      } finally {
        setDecodeLoading(false);
      }
    },
    [onDecode]
  );

  // ---- Section ----
  const loadSection = useCallback(async (name) => {
    if (!name?.trim()) return;
    setSectionLoading(true);
    setSectionError(null);
    setSectionData(null);
    setSectionName(name.trim());
    try {
      const res = await Gateway.http.imcon.get(
        `/api/health/image_lang/section/${encodeURIComponent(name.trim())}`
      );
      const data = res?.data?.data || res?.data;
      if (data && Array.isArray(data.checks)) {
        setSectionData(data);
      } else if (data?.error) {
        setSectionError(data.error);
      } else {
        setSectionError('Неожиданный ответ сервера');
      }
    } catch (err) {
      const status = err.response?.status;
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message;
      setSectionError(status === 404 ? `Секция не найдена: ${name}` : `Ошибка: ${msg}`);
    } finally {
      setSectionLoading(false);
    }
  }, []);

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            🖼️ ImageLang
          </Typography>
          <Chip label="v10.8" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Перезагрузить полный отчёт">
            <IconButton size="small" onClick={loadReport} disabled={reportLoading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 32,
            mb: 1,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0.5,
              fontSize: '0.7rem',
              textTransform: 'none',
              minWidth: 'auto',
              px: 1.5,
            },
          }}
        >
          <Tab label="Отчёт" />
          <Tab label="Encode" />
          <Tab label="Decode" />
          <Tab label="Секция" />
        </Tabs>

        {tab === TABS.REPORT && (
          <FullReportTab
            report={report}
            loading={reportLoading}
            error={reportError}
            onRefresh={loadReport}
          />
        )}
        {tab === TABS.ENCODE && (
          <EncodeTab
            onEncode={handleEncode}
            encodeResult={encodeResult}
            encodeLoading={encodeLoading}
            encodeError={encodeError}
          />
        )}
        {tab === TABS.DECODE && (
          <DecodeTab
            onDecode={handleDecode}
            decodeResult={decodeResult}
            decodeLoading={decodeLoading}
            decodeError={decodeError}
          />
        )}
        {tab === TABS.SECTION && (
          <SectionTab
            sectionName={sectionName}
            sectionData={sectionData}
            sectionLoading={sectionLoading}
            sectionError={sectionError}
            onLoad={loadSection}
          />
        )}
      </CardContent>
    </Card>
  );
}
